/**
 * Stores the route a user was trying to reach before being sent through a
 * login flow, so they can be returned to it afterwards.
 *
 * WoningNet SSO always redirects back to a fixed callback URL (/woningnet),
 * so the intended destination cannot travel along with the OAuth redirect.
 * sessionStorage survives the full page reload that the redirect causes,
 * within the same tab and origin.
 */

const STORAGE_KEY = 'dvv_post_login_redirect';

/**
 * Only same-origin relative paths are accepted, to avoid turning the login
 * flow into an open redirect.
 */
const isSafeRedirectPath = (path: string): boolean =>
  path.startsWith('/') && !path.startsWith('//');

/**
 * Remember where to send the user once they have logged in.
 */
export const setPostLoginRedirect = (path: string): void => {
  if (typeof sessionStorage === 'undefined' || !isSafeRedirectPath(path)) {
    return;
  }
  sessionStorage.setItem(STORAGE_KEY, path);
};

/**
 * Read and clear the remembered destination. Returns null when there is none.
 */
export const consumePostLoginRedirect = (): string | null => {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  const stored = sessionStorage.getItem(STORAGE_KEY);
  sessionStorage.removeItem(STORAGE_KEY);
  if (!stored || !isSafeRedirectPath(stored)) {
    return null;
  }
  return stored;
};
